import React, { useState, useCallback } from 'react';

import type { WizardProps } from './types';

/**
 * Wizard — multi-step com configuração nonSkippableSteps.
 *
 * Pattern DS v2.2.0 — primitivo universal para fluxos guiados.
 * Usado em Brief 5 B5 Anonymization 5 steps + Brief 4 R1 PlaybookSimulator.
 *
 * nonSkippableSteps: índices 0-based de steps onde o botão "Próximo"
 * só destrava quando validate() retorna null (pattern §1.2 item 3 inegociável).
 *
 * Acessibilidade:
 * - aria-current="step" no step ativo do rail
 * - Botões desabilitados com aria-disabled quando step não-pulável não validado
 */
export function Wizard({
  steps,
  nonSkippableSteps = [],
  onComplete,
  onCancel,
  nextLabel = 'Próximo',
  backLabel = 'Voltar',
  completeLabel = 'Concluir',
  cancelLabel = 'Cancelar',
  showStepNumbers = true,
}: WizardProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);

  const isNonSkippable = nonSkippableSteps.includes(currentIndex);
  const isLastStep = currentIndex === steps.length - 1;
  const currentStep = steps[currentIndex];

  const handleNext = useCallback(() => {
    const step = steps[currentIndex];
    if (step === undefined) return; // índice fora do range — nada a avançar
    if (step.validate) {
      const error = step.validate();
      if (error) {
        setValidationError(error);
        return;
      }
    }
    // Also check nonSkippable without custom validate
    if (isNonSkippable && !step.validate) {
      // nonSkippable without validate means step has no guard — proceed
    }
    setValidationError(null);
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentIndex((i) => i + 1);
    }
  }, [currentIndex, isLastStep, isNonSkippable, onComplete, steps]);

  const handleBack = useCallback(() => {
    setValidationError(null);
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, []);

  // Check if Next is blocked (nonSkippable with failing validate)
  const isNextBlocked =
    isNonSkippable && currentStep?.validate !== undefined && currentStep.validate() !== null;

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    fontFamily: 'var(--font-sans, system-ui)',
  };

  const railStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2, 8px)',
    padding: 'var(--space-4, 16px) var(--space-4, 16px) 0',
    marginBottom: 'var(--space-4, 16px)',
  };

  const bodyStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '0 var(--space-4, 16px)',
  };

  const footerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--space-4, 16px)',
    borderTop: '1px solid var(--border-default, #e5e7eb)',
    marginTop: 'var(--space-4, 16px)',
  };

  const btnBase: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--space-2, 8px)',
    padding: '0 var(--space-4, 16px)',
    height: 32,
    fontSize: 'var(--text-sm, 12px)',
    fontWeight: 'var(--fw-medium, 500)',
    borderRadius: 'var(--radius-md, 7px)',
    cursor: 'pointer',
    border: '1px solid var(--border-default, #e5e7eb)',
    background: 'var(--bg-surface, #fff)',
    color: 'var(--fg-1, #111)',
  };

  const btnPrimaryStyle: React.CSSProperties = {
    ...btnBase,
    background: isNextBlocked ? 'var(--bg-subtle, #f9fafb)' : 'var(--brand, #d97706)',
    color: isNextBlocked ? 'var(--fg-muted, #9ca3af)' : 'var(--fg-on-accent, #fff)',
    border: 'none',
    cursor: isNextBlocked ? 'not-allowed' : 'pointer',
    fontWeight: 'var(--fw-semibold, 600)',
    opacity: isNextBlocked ? 0.6 : 1,
  };

  return (
    <div style={containerStyle} data-testid="wizard">
      {/* Step Rail */}
      <nav aria-label="Progresso" style={railStyle}>
        {steps.map((step, index) => {
          const isActive = index === currentIndex;
          const isCompleted = index < currentIndex;
          return (
            <React.Fragment key={step.id}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2, 8px)' }}
                aria-current={isActive ? 'step' : undefined}
                data-testid={`wizard-step-${index}`}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 'var(--radius-pill, 999px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 'var(--text-xs, 11px)',
                    fontWeight: 'var(--fw-semibold, 600)',
                    background: isCompleted
                      ? 'var(--success-bg, #dcfce7)'
                      : isActive
                        ? 'var(--brand, #d97706)'
                        : 'var(--bg-subtle, #f9fafb)',
                    color: isCompleted
                      ? 'var(--success-fg, #15803d)'
                      : isActive
                        ? 'var(--fg-on-accent, #fff)'
                        : 'var(--fg-3, #6b7280)',
                    border: isActive ? 'none' : '1px solid var(--border-default, #e5e7eb)',
                    flexShrink: 0,
                  }}
                >
                  {isCompleted ? '✓' : showStepNumbers ? index + 1 : '•'}
                </div>
                <span
                  style={{
                    fontSize: 'var(--text-sm, 12px)',
                    fontWeight: isActive ? 'var(--fw-medium, 500)' : 'var(--fw-regular, 400)',
                    color: isActive ? 'var(--fg-1, #111)' : 'var(--fg-3, #6b7280)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  style={{
                    flex: 1,
                    height: 1,
                    background: isCompleted
                      ? 'var(--brand, #d97706)'
                      : 'var(--border-default, #e5e7eb)',
                    minWidth: 8,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </nav>

      {/* Body */}
      <div style={bodyStyle} data-testid="wizard-body">
        {currentStep?.content}
        {validationError && (
          <p
            role="alert"
            style={{
              color: 'var(--danger-fg, #dc2626)',
              fontSize: 'var(--text-sm, 12px)',
              marginTop: 'var(--space-2, 8px)',
            }}
            data-testid="wizard-validation-error"
          >
            {validationError}
          </p>
        )}
      </div>

      {/* Footer */}
      <div style={footerStyle}>
        <div style={{ display: 'flex', gap: 'var(--space-2, 8px)' }}>
          {onCancel && (
            <button type="button" onClick={onCancel} style={btnBase} data-testid="wizard-cancel">
              {cancelLabel}
            </button>
          )}
          {currentIndex > 0 && (
            <button type="button" onClick={handleBack} style={btnBase} data-testid="wizard-back">
              {backLabel}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={handleNext}
          disabled={isNextBlocked}
          aria-disabled={isNextBlocked}
          style={btnPrimaryStyle}
          data-testid="wizard-next"
        >
          {isLastStep ? completeLabel : nextLabel}
        </button>
      </div>
    </div>
  );
}
